import {Injectable} from '@angular/core';
import OverlayModel from '../../../models/overlay.model';
import {FrameService} from './frame.service';
import {AnimationType} from '../../../enums/animation.enum';
import {EasingService} from './easing.service';
import {AnimationProgressCoordinateInterface} from '../../../interfaces/animation-progress-coordinate.interface';

@Injectable({
    providedIn: 'root'
})
export class AnimationService {

    constructor(private frameService: FrameService, private easingService: EasingService) {
    }

    getObjectWithAnimationInCoordinate(overlay): AnimationProgressCoordinateInterface | {} {
        switch (overlay.animations.start.type) {
            case AnimationType.SLIDE_IN_LEFT:
                return this.getAnimationInSteps(overlay, {
                    x: 0 - (overlay.size.width * 2),
                    y: overlay.position.top
                }, false);
            case AnimationType.SLIDE_IN_RIGHT:
                return this.getAnimationInSteps(overlay, {
                    x: 100 + overlay.size.width,
                    y: overlay.position.top
                }, false);
            case AnimationType.SLIDE_IN_BOTTOM:
                return this.getAnimationInSteps(overlay, {
                    x: overlay.position.left,
                    y: 100 + overlay.size.height
                }, true);
            case AnimationType.SLIDE_IN_TOP:
                return this.getAnimationInSteps(overlay, {
                    x: overlay.position.left,
                    y: 0 - (overlay.size.height * 2)
                }, true);
            default:
                return {
                };
        }
    }

    getObjectWithAnimationOutCoordinate(overlay): AnimationProgressCoordinateInterface | {} {
        switch (overlay.animations.end.type) {
            case AnimationType.SLIDE_OUT_LEFT:
                return this.getAnimationOutSteps(overlay, false);
            case AnimationType.SLIDE_OUT_RIGHT:
                return this.getAnimationOutSteps(overlay, false);
            case AnimationType.SLIDE_OUT_BOTTOM:
                return this.getAnimationOutSteps(overlay, true);
            case AnimationType.SLIDE_OUT_TOP:
                return this.getAnimationOutSteps(overlay, true);
            default:
                return {
                };
        }
    }

    private getAnimationOutSteps(overlay: OverlayModel, isVertical): AnimationProgressCoordinateInterface {
        let progress = 0;

        const startFrame = this.frameService.getRoundFrame(overlay.time.end - (overlay.animations.end.duration));
        const endFrame = this.frameService.getRoundFrame(overlay.time.end);
        const framesCount = this.frameService.numberOfFrameInAnimation(endFrame, startFrame);
        const coordinate = {
            x: overlay.position.left, y: overlay.position.top
        };
        const animation = {
        } as AnimationProgressCoordinateInterface;

        animation[startFrame] = coordinate;

        for (let i = startFrame; i <= endFrame; i++) {
            progress = (i - startFrame) / framesCount;
            animation[i] = {
                x: this.getXPositionOut(overlay, coordinate.x, isVertical, progress),
                y: this.getYPositionOut(overlay, coordinate.y, isVertical, progress)
            };
        }

        return animation;
    }

    private getAnimationInSteps(overlay, coordinate, isVertical): AnimationProgressCoordinateInterface {
        let progress = 0;

        const animation = {
        } as AnimationProgressCoordinateInterface;
        const startFrame = this.frameService.getRoundFrame(overlay.time.start);
        const endFrame = this.frameService.getRoundFrame(overlay.time.start + (overlay.animations.start.duration));
        const framesCount = this.frameService.numberOfFrameInAnimation(endFrame, startFrame);

        animation[startFrame] = coordinate;

        for (let i = startFrame; i <= endFrame; i++) {
            progress = (i - startFrame) / framesCount;
            animation[i] = {
                x: this.getXPositionIn(overlay, coordinate.x, isVertical, progress),
                y: this.getYPositionIn(overlay, coordinate.y, isVertical, progress)
            };
        }
        return animation;
    }

    private getXPositionIn(overlay, coordinateX, isVertical, progress): number {
        return isVertical ? overlay.position.left : coordinateX
      + this.easingService.calculateProgressByType(overlay.animations.start.easeType, progress)
      * (overlay.position.left - coordinateX);
    }

    private getXPositionOut(overlay, coordinateX, isVertical, progress): number {
        return isVertical ? overlay.position.left : coordinateX
      + this.easingService.calculateProgressByType(overlay.animations.end.easeType, progress)
      * this.getAnimationMultiplier(overlay);
    }

    private getYPositionOut(overlay, coordinateY, isVertical, progress): number {
        return isVertical ? coordinateY
      - this.easingService.calculateProgressByType(overlay.animations.start.easeType, progress)
      * this.getAnimationMultiplier(overlay) : overlay.position.top;
    }

    private getYPositionIn(overlay, coordinateY, isVertical, progress): number {
        return isVertical ? coordinateY
      - this.easingService.calculateProgressByType(overlay.animations.start.easeType, progress)
      * (coordinateY - overlay.position.top) : overlay.position.top;
    }

    private getAnimationMultiplier(overlay): number {
        switch (overlay.animations.end.type) {
            case AnimationType.SLIDE_OUT_LEFT:
                return - (overlay.position.left + overlay.size.width);
            case AnimationType.SLIDE_OUT_RIGHT:
                return 100 - (overlay.position.left);
            case AnimationType.SLIDE_OUT_BOTTOM:
                return overlay.position.top - 100;
            case AnimationType.SLIDE_OUT_TOP:
                return (overlay.position.top + overlay.size.height);
            default:
                return 1;
        }
    }
}
